// Signing into the console.
//
// The flow is OAuth 2.0 authorization code with PKCE, and the half of it that
// is a server-to-server call happens on the API rather than here: see
// apps/tprm-api/internal/api/auth.go for why. This file owns the browser half,
// which is the redirect, the verifier, and where the resulting token is kept.
//
// WHERE THE TOKEN IS KEPT, AND WHY THAT IS NOT localStorage
//
// sessionStorage. It is scoped to the tab, so closing it ends the session, and
// it is not shared with another tab that somebody left open on a shared
// machine. localStorage would survive both, which is convenient exactly until
// the laptop is borrowed. The cost is that a new tab has to sign in again, and
// that is the right trade for a console showing a customer's vendor register.
//
// A refresh token is held the same way and used only to renew an access token
// that is about to expire. Neither is ever written to a URL, and nothing here
// puts a token in a query string.

import { endQueue } from './workQueue'

export type AuthConfig =
  | { mode: 'dev'; ready: true }
  | { mode: 'oidc'; ready: false; reason: string }
  | {
      mode: 'oidc'
      ready: true
      issuer: string
      clientId: string
      authorizationUrl: string
      endSessionUrl: string
      // Which parameter the provider's end-session endpoint takes for where
      // to land afterwards. The server knows its provider; this file must
      // not, or a Cognito estate and a Keycloak one need two consoles.
      endSessionRedirectParam?: string
      scopes: string
      redirectUri: string
      // The API the token is for, and the parameter the provider reads it
      // from: audience (Okta, Auth0), resource (ADFS, which ignores audience)
      // or none. Absent means audience, which is what every API before this
      // setting served.
      audience: string
      audienceParam?: 'audience' | 'resource' | 'none'
      // False only for a provider that predates PKCE (ADFS 2016), where the
      // operator switched it off. Absent means on.
      pkce?: boolean
      // True when the provider is a Cognito user pool the API can ask with an
      // email and a password on the console's own form. Absent or false, the
      // hosted page is the only way in.
      passwordSignIn?: boolean
      // TPRM's own accounts (AUTH_MODE=accounts). sso says whether single
      // sign-on is offered right now; ssoSource and ssoName say through whom
      // (INHERITED from FOCTTA Core, CONFIGURED in TPRM, or the DEPLOYMENT's);
      // ssoUnavailable says why not when it is not.
      accounts?: boolean
      sso?: boolean
      ssoSource?: string
      ssoName?: string
      ssoUnavailable?: string
      passwordPolicy?: string
    }

const VERIFIER_KEY = 'tprm.pkce.verifier'
const STATE_KEY = 'tprm.pkce.state'
const RETURN_KEY = 'tprm.pkce.return'
const ACCESS_KEY = 'tprm.oidc.access'
const REFRESH_KEY = 'tprm.oidc.refresh'
const EXPIRY_KEY = 'tprm.oidc.expiry'
// Set when the session in this tab was opened from Consent rather than by
// signing in here. It decides how signing out works: a session from Consent is
// ended on this product's API, and there is no identity provider page to send
// the reader to afterwards.
const LAUNCH_KEY = 'tprm.launch'

function read(key: string): string {
  try {
    return sessionStorage.getItem(key) || ''
  } catch {
    // A hardened browser profile blocks storage entirely. Returning empty
    // sends the reader to the sign-in screen, which is a truthful answer: with
    // nowhere to hold a token, there is no session.
    return ''
  }
}

function write(key: string, value: string): void {
  try {
    if (value) sessionStorage.setItem(key, value)
    else sessionStorage.removeItem(key)
  } catch {
    /* see read: without storage there is no session to keep */
  }
}

export async function loadAuthConfig(): Promise<AuthConfig> {
  const res = await fetch('/v1/auth/config', { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error('The API would not say how to sign in (HTTP ' + res.status + ').')
  return (await res.json()) as AuthConfig
}

// base64url without padding, which is what both the PKCE challenge and the
// state parameter have to be.
function base64url(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomString(bytes = 32): string {
  const buf = new Uint8Array(bytes)
  crypto.getRandomValues(buf)
  return base64url(buf)
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64url(new Uint8Array(digest))
}

// The redirect the provider is told to send the browser back to. It has to
// match exactly, character for character, at the authorize call and again at
// the token exchange, and it has to be registered on the provider's client.
// The configured value wins; without one this is the console's own origin and
// path, with no fragment, because a fragment never reaches the provider and a
// query string would not survive the redirect intact.
export function redirectUri(cfg: AuthConfig): string {
  if (cfg.mode === 'oidc' && cfg.ready && cfg.redirectUri) return cfg.redirectUri
  return window.location.origin + window.location.pathname
}

export async function beginSignIn(cfg: AuthConfig): Promise<void> {
  if (cfg.mode !== 'oidc' || !cfg.ready) throw new Error('This build has no identity provider to sign in against.')

  const pkce = cfg.pkce !== false
  const verifier = pkce ? randomString(48) : ''
  const state = randomString(16)
  write(VERIFIER_KEY, verifier)
  write(STATE_KEY, state)
  // Where the reader was heading. The fragment is the console's own route, and
  // it survives the round trip only because we put it somewhere first.
  write(RETURN_KEY, window.location.hash || '#/portfolio')

  const url = new URL(cfg.authorizationUrl)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('client_id', cfg.clientId)
  url.searchParams.set('redirect_uri', redirectUri(cfg))
  url.searchParams.set('scope', cfg.scopes || 'openid profile email')
  url.searchParams.set('state', state)
  if (pkce) {
    url.searchParams.set('code_challenge', await challengeFor(verifier))
    url.searchParams.set('code_challenge_method', 'S256')
  }
  // One parameter, never both: ADFS refuses nothing it does not read, so an
  // audience it ignores signs the person in to a token TPRM then rejects.
  const param = cfg.audienceParam || 'audience'
  if (cfg.audience && param !== 'none') url.searchParams.set(param, cfg.audience)

  window.location.assign(url.toString())
}

export type CompletionResult =
  | { status: 'none' }
  | { status: 'signed-in' }
  | { status: 'failed'; message: string }

// completeSignIn is called once on load. It does nothing unless the address bar
// carries a code the provider has just put there.
export async function completeSignIn(): Promise<CompletionResult> {
  const params = new URLSearchParams(window.location.search)
  const code = params.get('code')
  const returnedState = params.get('state')
  const providerError = params.get('error')

  if (providerError) {
    clearQuery()
    return {
      status: 'failed',
      message: params.get('error_description') || 'The identity provider refused the sign-in (' + providerError + ').',
    }
  }
  if (!code) return { status: 'none' }

  const expected = read(STATE_KEY)
  write(STATE_KEY, '')
  const verifier = read(VERIFIER_KEY)
  write(VERIFIER_KEY, '')

  if (!expected || returnedState !== expected) {
    // The state did not come from this tab. Either a stale redirect or a code
    // somebody else obtained; exchanging it would be signing in as whoever
    // sent the link.
    clearQuery()
    return { status: 'failed', message: 'This sign-in did not start in this tab. Try again from the console.' }
  }

  const cfg = await loadAuthConfig()
  const pkce = !(cfg.mode === 'oidc' && cfg.ready && cfg.pkce === false)
  if (!verifier && pkce) {
    clearQuery()
    return { status: 'failed', message: 'The sign-in could not be completed because this tab lost its verifier.' }
  }
  const res = await fetch('/v1/auth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ code, codeVerifier: verifier, redirectUri: redirectUri(cfg) }),
  })

  const body = (await res.json().catch(() => null)) as {
    accessToken?: string
    refreshToken?: string
    expiresIn?: number
    message?: string
    remedy?: string
    error?: string
  } | null

  clearQuery()
  if (!res.ok || !body?.accessToken) {
    // No TPRM account, or a switched-off one: the sign-in worked and the
    // organisation has not given this person access. Said whole, with who to
    // ask, rather than as a failure to retry.
    if (body?.error === 'NO_TPRM_ACCESS') {
      return { status: 'failed', message: [body.message, body.remedy].filter(Boolean).join(' ') }
    }
    return { status: 'failed', message: body?.message || 'The sign-in could not be completed.' }
  }

  store(body.accessToken, body.refreshToken || '', body.expiresIn || 0)
  const back = read(RETURN_KEY) || '#/portfolio'
  write(RETURN_KEY, '')
  window.location.hash = back
  return { status: 'signed-in' }
}

// Arriving from Consent.
//
// Consent sends the browser to /launch?code=... with a one-time code. The code
// is taken out of the address bar before anything else happens, so it is not
// left in history or in whatever the reader copies next, and it is posted to
// the API, which redeems it with Consent server to server. What comes back is
// a session token kept exactly where a sign-in keeps its own. The code is
// useless to anybody who copies it: it can be redeemed once, within a minute,
// and only by this product's key.
// What somebody following an old launch link reads, now the launch is
// withdrawn (release 3): third-party risk has its own sign-in.
export const LAUNCH_WITHDRAWN =
  'Opening third-party risk from Consent with a one-time link has been withdrawn. Sign in here with your organisation\'s sign-in.'

export function isLaunchPath(): boolean {
  return window.location.pathname === '/launch' || window.location.pathname === '/launch/'
}

export function isLaunchSession(): boolean {
  return read(LAUNCH_KEY) === '1'
}

export async function completeLaunch(): Promise<CompletionResult> {
  if (!isLaunchPath()) return { status: 'none' }
  const params = new URLSearchParams(window.location.search)
  const code = params.get('code') || ''
  // Which Consent organisation the person came from, where the link says. A
  // deployment paired with more than one Consent redeems the code with that
  // organisation's Consent and no other; without it, with its own.
  const consentTenantId = params.get('tid') || ''
  window.history.replaceState(null, '', window.location.origin + '/')

  if (!code) {
    return { status: 'failed', message: 'This link carries no sign-in. Open third-party risk from Consent again.' }
  }
  let res: Response
  try {
    res = await fetch('/v1/auth/launch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(consentTenantId ? { code, consentTenantId } : { code }),
    })
  } catch {
    return { status: 'failed', message: 'Third-party risk could not be reached. Try again from Consent.' }
  }
  if (res.status === 404) {
    // The launch is withdrawn: a release build of the API does not serve it
    // (apps/tprm-api/internal/api/suite_launch_withdrawn.go). The person is
    // told where to go instead of a sign-in failure they cannot act on.
    return { status: 'failed', message: LAUNCH_WITHDRAWN }
  }
  const body = (await res.json().catch(() => null)) as {
    accessToken?: string
    expiresIn?: number
    message?: string
  } | null
  if (!res.ok || !body?.accessToken) {
    return { status: 'failed', message: body?.message || 'You could not be signed in from Consent.' }
  }
  store(body.accessToken, '', body.expiresIn || 0)
  write(LAUNCH_KEY, '1')
  // No hash, so the console sends the reader to the first screen their roles
  // open, which is what it does for every other arrival.
  window.location.hash = ''
  return { status: 'signed-in' }
}

// The console's own sign-in form keeps its result exactly where the redirect
// flow keeps its own, so everything downstream reads one session.
export function storeSession(access: string, refresh: string, expiresIn: number): void {
  store(access, refresh, expiresIn)
}

function store(access: string, refresh: string, expiresIn: number): void {
  write(ACCESS_KEY, access)
  write(REFRESH_KEY, refresh)
  // Thirty seconds early, so a request is never sent with a token that expires
  // while it is in flight.
  const expiry = expiresIn > 0 ? Date.now() + (expiresIn - 30) * 1000 : 0
  write(EXPIRY_KEY, expiry ? String(expiry) : '')
}

function clearQuery(): void {
  // The code and state are single use and spent. Leaving them in the address
  // bar puts them in browser history and in whatever the reader pastes next.
  const clean = window.location.origin + window.location.pathname + window.location.hash
  window.history.replaceState(null, '', clean)
}

export function accessToken(): string {
  return read(ACCESS_KEY)
}

export function isSignedIn(): boolean {
  return accessToken() !== ''
}

export async function signOut(cfg: AuthConfig | null): Promise<void> {
  // My work's "Next of N" names the signed-in person's tasks: it goes with them.
  endQueue()
  const launched = isLaunchSession()
  const token = accessToken()
  if (token.startsWith('tprm_as_')) {
    // A session TPRM issued to one of its own accounts: ended on the API, so
    // it stops working everywhere. Single sign-on itself is ended where it
    // lives (FOCTTA Core, or the organisation's provider), so nobody is sent
    // anywhere else.
    write(ACCESS_KEY, '')
    write(REFRESH_KEY, '')
    write(EXPIRY_KEY, '')
    write(LAUNCH_KEY, '')
    await fetch('/v1/auth/session/end', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token },
    }).catch(() => undefined)
    window.location.reload()
    return
  }
  write(ACCESS_KEY, '')
  write(REFRESH_KEY, '')
  write(EXPIRY_KEY, '')
  write(LAUNCH_KEY, '')
  if (launched) {
    // Ended on the API, so the session stops working everywhere and not only
    // in this tab. Signing out of Consent is Consent's to do, so the reader is
    // not sent anywhere else.
    await fetch('/v1/auth/launch/end', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token },
    }).catch(() => undefined)
    window.location.reload()
    return
  }
  if (cfg && cfg.mode === 'oidc' && cfg.ready && cfg.endSessionUrl) {
    // Ending the session at the provider as well, or the next sign-in is
    // silent and the reader believes they signed out.
    const url = new URL(cfg.endSessionUrl)
    url.searchParams.set('client_id', cfg.clientId)
    url.searchParams.set(
      cfg.endSessionRedirectParam || 'post_logout_redirect_uri',
      window.location.origin + window.location.pathname,
    )
    window.location.assign(url.toString())
    return
  }
  window.location.reload()
}

let refreshing: Promise<string> | null = null

// ensureFreshToken renews the access token when it is about to expire. It is
// awaited before every API call, and it is a no-op in the common case: one
// comparison against a timestamp already in memory.
//
// The in-flight promise is shared so that a screen firing six requests at once
// performs one refresh rather than six, five of which would present a refresh
// token the provider has already rotated away.
export async function ensureFreshToken(): Promise<string> {
  const token = accessToken()
  const expiry = Number(read(EXPIRY_KEY) || '0')
  if (!token || !expiry || Date.now() < expiry) return token

  const refresh = read(REFRESH_KEY)
  if (!refresh) return token

  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch('/v1/auth/token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ refreshToken: refresh }),
        })
        if (!res.ok) return ''
        const body = (await res.json()) as { accessToken?: string; refreshToken?: string; expiresIn?: number }
        if (!body.accessToken) return ''
        store(body.accessToken, body.refreshToken || refresh, body.expiresIn || 0)
        return body.accessToken
      } catch {
        return ''
      } finally {
        refreshing = null
      }
    })()
  }
  const renewed = await refreshing
  return renewed || token
}
